import {
	ArrowRightOutlined,
	CarryOutOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	EnvironmentOutlined,
	FieldTimeOutlined,
	FileTextOutlined,
	LogoutOutlined,
	TableOutlined,
	UserAddOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Card, Skeleton, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	ATTENDANCE_STATUS_RANG,
	ColumnChart,
	holatVar,
	SegmentBar,
	Sparkline,
} from "@/shared/charts";
import { useInView } from "../../hooks/useInView";
import { useReportAnalytics } from "../../hooks/useReports";
import type { ReportAnalytics, ReportDescriptorView } from "../../types";
import type { ReportGroupKey } from "../../utils/reportGroups";
import { reportDescription, reportTitle } from "../../utils/reportI18n";
import { cardStat, ikonkaFoni, OCH_QIZIL, sonMatni, totalMatni } from "./hubFormat";

const { Text } = Typography;

type Guruh = ReportGroupKey | "other";

/** Kind → ikonka va ohang (canvas: davomat ko'k/binafsha, xavf qizil, dala yashil-moviy). */
const KORINISH: Record<string, { icon: ReactNode; color: string }> = {
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	attendance_daily: { icon: <CheckCircleOutlined />, color: holatVar("kelgan") },
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	attendance_monthly: { icon: <TableOutlined />, color: holatVar("kelgan") },
	lateness: { icon: <FieldTimeOutlined />, color: holatVar("kechikkan") },
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	work_time: { icon: <ClockCircleOutlined />, color: holatVar("kechikkan") },
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	zone_exits: { icon: <LogoutOutlined />, color: holatVar("kelmagan") },
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	fake_location: { icon: <EnvironmentOutlined />, color: holatVar("kelmagan") },
	violations: { icon: <WarningOutlined />, color: holatVar("kelmagan") },
	tasks: { icon: <CarryOutOutlined />, color: holatVar("uzrli") },
	leads: { icon: <UserAddOutlined />, color: holatVar("uzrli") },
};

const ZAXIRA_KORINISH = { icon: <FileTextOutlined />, color: holatVar("neytral") };

/** Xavfsizlik ustunlaridagi «ajratilgan» qism yorlig'i — `analytics.totals` kaliti. */
const FLAGGED_KEY: Record<string, string> = {
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	zone_exits: "unpermitted",
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti snake_case (kontrakt §3.1)
	fake_location: "high",
	violations: "high",
};

/** `tasks` breakdown holati → rang (`task_status`). */
const TASK_RANG: Record<string, string> = {
	faol: holatVar("kelgan"),
	yakunlangan: holatVar("uzrli"),
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	bekor_qilingan: holatVar("neytral"),
};

export interface ReportCardProps {
	descriptor: ReportDescriptorView;
	group: Guruh;
	range: { dateFrom: string; dateTo: string };
}

function qisqaSana(sana: string): string {
	return dayjs(sana).format("DD.MM");
}

/**
 * Markazdagi bitta hisobot kartasi: ikonka, nom, tavsif, teglar,
 * mini-diagramma, asosiy son va «Ochish» havolasi.
 *
 * ⚠️ Analitika faqat karta ko'rinish oynasiga kirganda so'raladi
 * (`useInView`). Xato (403/404) yoki bo'sh ma'lumotda mini-diagramma
 * va son JIMGINA yashiriladi — karta baribir hisobotga olib boradi.
 *
 * ⚠️ Havola tanlangan davr bilan: `/reports/$kind?dateFrom&dateTo`.
 */
export function ReportCard({ descriptor, group, range }: ReportCardProps) {
	const { t } = useTranslation("reports");
	const ref = useRef<HTMLDivElement>(null);
	const inView = useInView(ref);
	const { data, isLoading, isError } = useReportAnalytics(descriptor.kind, range, inView);
	const korinish = KORINISH[descriptor.kind] ?? ZAXIRA_KORINISH;
	const stat = data && !isError ? cardStat(descriptor.kind, data.totals) : undefined;

	return (
		<div ref={ref} className="h-full" data-report-card={descriptor.kind}>
			<Card size="small" className="h-full" styles={{ body: { height: "100%" } }}>
				<div className="flex h-full flex-col gap-3">
					<div className="flex items-start gap-3">
						<span
							aria-hidden="true"
							className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-base"
							style={{ backgroundColor: ikonkaFoni(korinish.color), color: korinish.color }}
						>
							{korinish.icon}
						</span>
						<div className="min-w-0 flex-1">
							<div data-report-title className="font-semibold text-[15px]">
								{reportTitle(t, descriptor)}
							</div>
							<Text type="secondary" className="text-[13px]">
								{reportDescription(t, descriptor)}
							</Text>
						</div>
					</div>

					<div className="flex flex-wrap gap-1.5">
						<Tag className="!m-0">{t(`list.scope.${descriptor.scopeDimension}`)}</Tag>
						<Tag className="!m-0">
							{t("list.columnsCount", { count: descriptor.columns.length })}
						</Tag>
						{descriptor.hasSummary ? <Tag className="!m-0">{t("hub.card.withSummary")}</Tag> : null}
					</div>

					<div className="min-h-[64px]">
						{isLoading && inView ? (
							<Skeleton.Node active className="!h-[46px] !w-full" />
						) : data && !isError ? (
							<MiniDiagramma kind={descriptor.kind} group={group} data={data} range={range} />
						) : null}
					</div>

					<div className="mt-auto flex items-center justify-between gap-2 border-[var(--chart-grid)] border-t pt-2.5">
						<div className="text-[13px]" data-card-stat>
							{stat ? (
								<>
									<Text type="secondary">{t(`analytics.totals.${stat.key}`)}: </Text>
									<b className="font-mono font-semibold">{totalMatni(stat.key, stat.value)}</b>
								</>
							) : null}
						</div>
						<Link
							to="/reports/$kind"
							params={{ kind: descriptor.kind }}
							search={{ dateFrom: range.dateFrom, dateTo: range.dateTo }}
							className="flex items-center gap-1 whitespace-nowrap font-medium text-[13px]"
						>
							{t("hub.card.open")}
							<ArrowRightOutlined aria-hidden="true" />
						</Link>
					</div>
				</div>
			</Card>
		</div>
	);
}

interface MiniProps {
	kind: string;
	group: Guruh;
	data: ReportAnalytics;
	range: { dateFrom: string; dateTo: string };
}

/**
 * Guruh bo'yicha mini-diagramma (reja D1):
 * davomat — `Sparkline` (`spark[0]`), xavfsizlik — `ColumnChart`
 * (daily primary/flagged), dala — `SegmentBar`.
 */
function MiniDiagramma({ kind, group, data, range }: MiniProps) {
	const { t } = useTranslation("reports");

	if (group === "field") {
		const segmentlar = dalaSegmentlari(kind, data, t);

		return segmentlar.length > 0 ? <SegmentBar segments={segmentlar} height={12} /> : <BoshMatn />;
	}

	if (group === "security") {
		if (data.daily.length === 0) {
			return <BoshMatn />;
		}

		const flaggedKey = FLAGGED_KEY[kind] ?? "high";
		const flaggedLabel = t(`analytics.totals.${flaggedKey}`);

		return (
			<div>
				<ColumnChart
					height={46}
					columns={data.daily.map((kun) => ({
						key: kun.date,
						label: "",
						stack: [
							{ value: kun.flagged, color: holatVar("kelmagan"), label: flaggedLabel },
							{
								value: Math.max(0, kun.primary - kun.flagged),
								color: OCH_QIZIL,
								label: t("hub.card.rest"),
							},
						],
						tooltip: `${qisqaSana(kun.date)} · ${sonMatni(kun.primary)} / ${flaggedLabel.toLocaleLowerCase()} ${sonMatni(kun.flagged)}`,
					}))}
				/>
				<OqYorliqlari kind={kind} range={range} />
			</div>
		);
	}

	const seriya = data.spark[0];

	if (!seriya || seriya.points.length < 2) {
		return <BoshMatn />;
	}

	const foiz = seriya.key === "rate";
	const rang = KORINISH[kind]?.color ?? holatVar("kelgan");

	return (
		<div>
			<Sparkline
				points={seriya.points}
				color={rang}
				height={46}
				area
				// ⚠️ Foizda shkala mutlaq bo'lishi kerak (Sparkline JSDoc):
				// 92↔93 tebranishi «qulash» bo'lib ko'rinmasin.
				{...(foiz
					? { min: Math.max(0, Math.floor(Math.min(95, ...seriya.points)) - 5), max: 100, goal: 95 }
					: {})}
				ariaLabel={`${t(`analytics.totals.${seriya.key}`)}: ${seriya.points.map(sonMatni).join(", ")}`}
			/>
			<OqYorliqlari kind={kind} range={range} />
		</div>
	);
}

function OqYorliqlari({
	kind,
	range,
}: {
	kind: string;
	range: { dateFrom: string; dateTo: string };
}) {
	const { t } = useTranslation("reports");

	return (
		<div className="mt-0.5 flex justify-between text-[11px]" style={{ color: "var(--chart-axis)" }}>
			<span>{qisqaSana(range.dateFrom)}</span>
			<span>{t(`hub.chartNote.${kind}`, { defaultValue: "" })}</span>
			<span>{qisqaSana(range.dateTo)}</span>
		</div>
	);
}

function BoshMatn() {
	const { t } = useTranslation("reports");

	return (
		<div
			className="flex h-[46px] items-center justify-center text-xs"
			style={{ color: "var(--chart-axis)" }}
		>
			{t("hub.card.noData")}
		</div>
	);
}

function dalaSegmentlari(
	kind: string,
	data: ReportAnalytics,
	t: TFunction
): { key: string; value: number; color: string; label: string }[] {
	// ⚠️ `leads` — canvas bo'yicha onlayn/oflayn ulushi (`totals` dan):
	// uning `breakdown` i forma sarlavhalari, rangini palitra bera olmaydi.
	if (kind === "leads") {
		const jami = data.totals.find((x) => x.key === "leads")?.value ?? 0;
		const oflayn = data.totals.find((x) => x.key === "offline")?.value ?? 0;

		if (jami <= 0) {
			return [];
		}

		return [
			{
				key: "online",
				value: Math.max(0, jami - oflayn),
				color: holatVar("kelgan"),
				label: t("hub.card.online"),
			},
			{
				key: "offline",
				value: oflayn,
				color: holatVar("ertaKetgan"),
				label: t("hub.card.offline"),
			},
		];
	}

	if (data.breakdown.every((b) => b.count <= 0)) {
		return [];
	}

	return data.breakdown.map((b) => ({
		key: b.key,
		value: b.count,
		color:
			TASK_RANG[b.key] ??
			(ATTENDANCE_STATUS_RANG[b.key]
				? holatVar(ATTENDANCE_STATUS_RANG[b.key] ?? "neytral")
				: holatVar("neytral")),
		label: t(`analytics.breakdown.${b.key}`),
	}));
}
